import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "../icons";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { T } from "../routes/Translate";

// Editar un hilo propio (Hilos.js y ProfilePage.js): el texto y quitar
// imágenes/videos. Las menciones y links guardados solo se conservan si su
// texto sigue en el hilo, igual que al publicar (handlePost en Hilos.js).
// Las republicaciones guardan una copia del original (originalText, etc.)
// en el doc de quien republicó, así que no se actualizan.
const EditThreadDialog = ({ thread, onClose, onSaved }) => {
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!thread) return;
    setText(thread.text || "");
    setImages(thread.images || []);
    setVideos(thread.videos || []);
  }, [thread]);

  const finalText = text.trim();
  const isEmpty = !finalText && images.length === 0 && videos.length === 0;

  const handleSave = async () => {
    if (!thread || isEmpty) return;
    setSaving(true);
    try {
      const changes = {
        text: finalText,
        images,
        videos,
        mentions: (thread.mentions || []).filter((m) => finalText.includes(`@${m.name}`)),
        links: (thread.links || []).filter((l) => finalText.includes(l.title)),
        editedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "threads", thread.id), changes);
      onSaved?.({ ...changes, editedAt: new Date() });
      onClose();
    } catch (error) {
      console.error("Error al editar el hilo:", error);
      alert("No se pudo guardar el hilo. Intentá de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const mediaThumb = (url, isVideo, onRemove) => (
    <Box key={url} sx={{ position: "relative", width: 88, height: 88, borderRadius: 2, overflow: "hidden", bgcolor: "#000" }}>
      {isVideo ? (
        <video src={url} muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      )}
      <IconButton
        size="small"
        onClick={onRemove}
        aria-label="quitar"
        sx={{ position: "absolute", top: 2, right: 2, bgcolor: "rgba(0,0,0,0.6)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );

  return (
    <Dialog open={Boolean(thread)} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle><T>Editar hilo</T></DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          multiline
          minRows={3}
          fullWidth
          value={text}
          onChange={(event) => setText(event.target.value)}
          disabled={saving}
          sx={{ mt: 1 }}
        />
        {(images.length > 0 || videos.length > 0) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            {images.map((url) => mediaThumb(url, false, () => setImages((prev) => prev.filter((u) => u !== url))))}
            {videos.map((url) => mediaThumb(url, true, () => setVideos((prev) => prev.filter((u) => u !== url))))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          <T>Cancelar</T>
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || isEmpty} sx={{ textTransform: "none" }}>
          {saving ? <CircularProgress size={20} color="inherit" /> : <T>Guardar</T>}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditThreadDialog;
